import { useMemo } from 'react';
import { useMediaQuery } from 'react-responsive';
import Draggable from 'react-draggable';
import { useAppSelector, useAppDispatch } from 'hooks/redux';
import useBenguide from 'hooks/benguide';
import { ChatBotContainer } from 'modules/chatbot/components';
import {
  setVisible as setChatVisible,
  selectChatUIState,
  selectIsFullScreen,
} from 'modules/chatbot/slices/chatbotSlice';
import usePlanRecommenderTabSync from 'modules/planRecommender/hooks/usePlanRecommenderTabSync';
import styles from './draggable.module.less';

const DraggableChatBot = () => {
  const { isEditMode, isAiAssistantChatEnabled } = useBenguide();
  const { isVisible: isChatOpen } = useAppSelector(selectChatUIState);
  const isFullScreen = useAppSelector(selectIsFullScreen);
  const dispatch = useAppDispatch();
  const isTabletOrMobile = useMediaQuery({ maxWidth: 1024 });

  // Initialize plan recommender tab sync to ensure chatbot can receive sync data
  // This runs in the background to listen for sync messages from other tabs
  usePlanRecommenderTabSync();

  // Calculate bounds for 90% of screen real estate
  const bounds = useMemo(() => {
    const screenWidth = window.innerWidth;
    const screenHeight = window.innerHeight;
    const topMargin = 0.01;
    const bottomMargin = 0.14;
    const sideMargin = 0.012;

    const chatWidth = 400;
    const chatHeight = 600;

    const initialLeft = screenWidth * 0.15 - chatWidth / 2;
    const initialTop = screenHeight * 0.5 - chatHeight / 2;

    return {
      left: screenWidth * sideMargin - initialLeft,
      top: screenHeight * topMargin - initialTop,
      right: screenWidth * (1 - sideMargin) - chatWidth - initialLeft,
      bottom: screenHeight * (1 - bottomMargin) - chatHeight - initialTop,
    };
  }, []);

  const getContainerClass = () => {
    let className = styles.draggableContainer;
    if (isFullScreen) {
      className = `${styles.draggableContainer} ${styles.draggableContainerFullScreen}`;
    } else {
      if (isChatOpen) className += ` ${styles.open}`;
    }
    if (isEditMode) className += ` ${styles.hidden}`;

    return className;
  };

  if (!isAiAssistantChatEnabled) {
    return <></>;
  }

  // Conditionally render with or without dragging based on screen size
  if (isTabletOrMobile && isAiAssistantChatEnabled) {
    return (
      <div className={getContainerClass()}>
        <ChatBotContainer
          visible={isChatOpen}
          setVisible={(visible) => dispatch(setChatVisible(visible))}
        />
      </div>
    );
  }

  return (
    <Draggable
      handle=".drag-handle"
      bounds={bounds}
      defaultPosition={{ x: 0, y: 0 }}
    >
      <div className={getContainerClass()}>
        <ChatBotContainer
          visible={isChatOpen}
          setVisible={(visible) => dispatch(setChatVisible(visible))}
        />
      </div>
    </Draggable>
  );
};

export default DraggableChatBot;
